// CallAssignPicker — the reusable "assign a call to a deal / nested project"
// picker. Extracted verbatim from the phone post-call wrap-up overlay
// (call-assign-link-v1) so BOTH the docked-phone wrap-up sheet AND the Calls
// list "DEAL OR PROJECT" cell (calls-list-assign-v1) drive the SAME search /
// nested-project probe / keyboard-nav / row-render mechanics.
//
// The component owns ALL picker-internal state (query, deal results, loading,
// active row, the ≤4-concurrent per-session project cache). The parent owns the
// STAGED selection + open flag and receives onSelect/onClear/onClose. Deals-only
// search; projects appear nested under their parent deal (buildAssignRows).
//
// Two placements:
//  - variant="phone"    → body-portaled floating card to the RIGHT of the docked
//    phone. Reuses the .phone-assign-* CSS. The docked phone sits at --phone-z
//    9998 and traps anything lower, so this portals OUTSIDE .phone-device and the
//    CSS already floats it at z 9999 / left:336 / bottom:24 (frozen contract —
//    zero behavior change vs the pre-extraction inline overlay).
//  - variant="anchored" → body-portaled next to a table cell (escapes table
//    overflow) at z 10001 (above the docked phone), positioned from anchorRect,
//    closes on outside-click / Escape. Optional onClear renders a "Clear
//    assignment" row.

import React, { useState, useRef, useMemo, useEffect, useCallback, useId } from 'react'
import { createPortal } from 'react-dom'
import { buildAssignRows, dealsToProbe, nextAssignActiveIndex, type AssignRow } from '../lib/phone/assignSearch'
import { CALLS_LIST_ASSIGN_MARKER } from '../lib/calls/callAssignCell'
import { fetchDeals, fetchDealProjects, type CrmDeal } from '../api'

export const CALL_ASSIGN_PICKER_MARKER = 'call-assign-picker-v1'

export type CallAssignPickerProps = {
  /** Whether the picker is visible. The component stays MOUNTED (so internal
   *  search/query state survives an open→close→open toggle within one session)
   *  and portals only while open. */
  open: boolean
  onClose: () => void
  /** A deal or nested-project row was chosen. */
  onSelect: (row: AssignRow) => void
  /** When provided, render a "Clear assignment" affordance (calls-list cell). */
  onClear?: () => void
  /** Empty-query "Suggested" deals for this call's contact. */
  suggestedDeals?: CrmDeal[]
  /** Name shown in the "Suggested — <name>" section header + empty copy. */
  suggestName?: string
  variant?: 'phone' | 'anchored'
  /** For variant="anchored": the trigger cell's rect to float against (initial
   *  position). */
  anchorRect?: DOMRect | null
  /** For variant="anchored": the live trigger element. When provided, the
   *  popover RE-MEASURES it on scroll/resize so it stays glued to the cell
   *  instead of detaching (rAF-throttled). Falls back to anchorRect if absent. */
  anchorEl?: HTMLElement | null
  /** Header title (default "Assign call"). */
  title?: string
}

// Cosmetic closed-won stage tag on a deal row (display only — NOT lifecycle
// classification, which lives server-side on pipeline_stages.semantics).
function isClosedWonDeal(d: CrmDeal): boolean {
  return /contract_signed|won/i.test(`${d.stage || ''} ${d.stageLabel || ''}`)
}

export default function CallAssignPicker({
  open,
  onClose,
  onSelect,
  onClear,
  suggestedDeals = [],
  suggestName = '',
  variant = 'phone',
  anchorRect = null,
  anchorEl = null,
  title = 'Assign call',
}: CallAssignPickerProps) {
  // Per-instance id namespace so two pickers open at once (phone wrap-up + a
  // calls-list cell) never mint duplicate DOM ids / aria-activedescendant
  // targets (listbox + per-row optionDomIds + the clear row).
  const uid = useId()
  const listboxId = `${uid}listbox`
  const clearDomId = `${uid}clear`
  const [query, setQuery] = useState('')
  const [deals, setDeals] = useState<CrmDeal[]>([])
  const [loading, setLoading] = useState(false)
  const [activeIdx, setActiveIdx] = useState(0)
  // Per-session cache of each deal's projects (or the 'loading' sentinel) + a
  // ≤4-concurrent lazy-fetch queue. `tick` bumps to re-render as fetches land.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const childrenCacheRef = useRef<Map<string, any[] | 'loading'>>(new Map())
  const childrenQueueRef = useRef<string[]>([])
  const childrenInflightRef = useRef(0)
  const [tick, setTick] = useState(0)
  const searchSeqRef = useRef(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  // Live anchor rect for variant="anchored" — re-measured on scroll/resize so
  // the popover tracks the trigger cell instead of detaching (review finding).
  const [liveRect, setLiveRect] = useState<DOMRect | null>(anchorRect)

  // Which deals feed the picker: typed results (≥2 chars) else the pre-seeded
  // "Suggested" set for this call's contact.
  const activeDeals = useMemo(
    () => (query.trim().length >= 2 ? deals : suggestedDeals),
    [query, deals, suggestedDeals],
  )
  const dealById = useMemo(() => {
    const m = new Map<string, CrmDeal>()
    for (const d of activeDeals) m.set(String(d.id), d)
    return m
  }, [activeDeals])
  // Flattened deal→project rows for the listbox — suggested ids boost-sorted first
  // (buildAssignRows). `tick` recomputes as project fetches land.
  const rows = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const byDeal = new Map<string, any[]>()
    for (const d of activeDeals) {
      const c = childrenCacheRef.current.get(String(d.id))
      if (Array.isArray(c)) byDeal.set(String(d.id), c)
    }
    return buildAssignRows(activeDeals, byDeal, { suggestedDealIds: suggestedDeals.map((d) => String(d.id)), idPrefix: `${uid}opt` })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDeals, suggestedDeals, tick, uid])

  // ≤4-concurrent lazy project fetch, cached per session.
  const pumpChildren = useCallback(() => {
    while (childrenInflightRef.current < 4 && childrenQueueRef.current.length > 0) {
      const dealId = childrenQueueRef.current.shift()!
      childrenInflightRef.current++
      fetchDealProjects(dealId)
        .then((res: { projects?: unknown[] } | null) => { childrenCacheRef.current.set(dealId, Array.isArray(res?.projects) ? res.projects : []) })
        .catch(() => { childrenCacheRef.current.set(dealId, []) })
        .finally(() => {
          childrenInflightRef.current--
          setTick((t) => t + 1)
          pumpChildren()
        })
    }
  }, [])
  const ensureDealProjects = useCallback((dealId: string) => {
    if (!dealId) return
    if (childrenCacheRef.current.has(dealId)) return
    childrenCacheRef.current.set(dealId, 'loading')
    childrenQueueRef.current.push(dealId)
    pumpChildren()
  }, [pumpChildren])

  // Debounced (350ms) DEALS search — seq guard drops stale responses.
  useEffect(() => {
    if (!open) return
    const q = query.trim()
    if (q.length < 2) { setDeals([]); setLoading(false); return }
    setLoading(true)
    const seq = ++searchSeqRef.current
    const t = setTimeout(() => {
      fetchDeals(0, 1, 8, { search: q, excludeClosed: false })
        .then((r) => { if (seq === searchSeqRef.current) setDeals(r.deals) })
        .catch(() => { if (seq === searchSeqRef.current) setDeals([]) })
        .finally(() => { if (seq === searchSeqRef.current) setLoading(false) })
    }, 350)
    return () => clearTimeout(t)
  }, [query, open])

  // Lazily fetch nested projects for each rendered deal (≤4 concurrent). Probe
  // UNCONDITIONALLY via dealsToProbe (the serving endpoints don't stamp
  // project_count, so a count gate silently hid nested projects — review finding #2).
  useEffect(() => {
    if (!open) return
    for (const id of dealsToProbe(activeDeals)) ensureDealProjects(id)
  }, [open, activeDeals, ensureDealProjects])

  // Autofocus the search input when the picker opens; reset the active row.
  useEffect(() => {
    if (open) { setActiveIdx(0); const t = setTimeout(() => inputRef.current?.focus(), 30); return () => clearTimeout(t) }
  }, [open])

  // Anchored variant: close on outside-click / Escape (the phone variant is
  // dismissed by its own close button + the review sheet lifecycle).
  useEffect(() => {
    if (!open || variant !== 'anchored') return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose() } }
    // Defer the mousedown listener a tick so the opening click doesn't self-close.
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    document.addEventListener('keydown', onKey)
    return () => { clearTimeout(t); document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open, variant, onClose])

  // Anchored variant: keep the popover glued to the trigger cell. Re-measure the
  // anchor (live element when supplied, else the captured rect) on scroll —
  // capture-phase so ANY scrolling ancestor fires, not just window — and on
  // resize, rAF-throttled. Listeners are torn down on close/unmount.
  useEffect(() => {
    if (!open || variant !== 'anchored') { return }
    let raf = 0
    const measure = () => { raf = 0; setLiveRect(anchorEl ? anchorEl.getBoundingClientRect() : anchorRect) }
    const schedule = () => { if (!raf) raf = requestAnimationFrame(measure) }
    measure() // initial sync (also picks up a fresh anchorRect/anchorEl prop)
    window.addEventListener('resize', schedule)
    window.addEventListener('scroll', schedule, true)
    return () => {
      if (raf) cancelAnimationFrame(raf)
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', schedule, true)
    }
  }, [open, variant, anchorEl, anchorRect])

  // Render helper for the flattened deal/project rows (typed + suggested states).
  const renderRows = () => (
    <>
      {rows.map((row: AssignRow, i: number) => {
        const active = i === activeIdx
        if (row.kind === 'project') {
          return (
            <div
              key={row.optionDomId}
              id={row.optionDomId}
              role="option"
              aria-selected={active}
              aria-label={`${row.label} — project under ${row.sublabel || 'deal'}`}
              className={`phone-assign-opt project${active ? ' active' : ''}`}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => onSelect(row)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg>
              <span className="phone-assign-opt-label">{row.label}</span>
            </div>
          )
        }
        const deal = dealById.get(String(row.id))
        const closedWon = deal ? isClosedWonDeal(deal) : false
        const loadingChildren = childrenCacheRef.current.get(String(row.id)) === 'loading'
        return (
          <React.Fragment key={row.optionDomId}>
            <div
              id={row.optionDomId}
              role="option"
              aria-selected={active}
              className={`phone-assign-opt deal${active ? ' active' : ''}`}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => onSelect(row)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
              <span className="phone-assign-opt-label">{row.label}</span>
              {row.sublabel && <span className="phone-assign-opt-sub">{row.sublabel}</span>}
              {closedWon && <span className="phone-assign-opt-tag">{deal?.stageLabel || 'Won'}</span>}
            </div>
            {loadingChildren && (
              <div className="phone-assign-child-shimmer"><span /><span /></div>
            )}
          </React.Fragment>
        )
      })}
    </>
  )

  if (!open) return null

  const inner = (
    <>
      <div className="phone-assign-overlay-head">
        <span className="phone-assign-overlay-title">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
          {title}
        </span>
        <button type="button" className="phone-assign-overlay-close" title="Close" onClick={onClose}>×</button>
      </div>
      <div className="phone-assign-search">
        <input
          ref={inputRef}
          type="text"
          className="phone-assign-input"
          role="combobox"
          aria-expanded
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeIdx === -1 ? (onClear ? clearDomId : undefined) : (rows[activeIdx]?.optionDomId || undefined)}
          placeholder="Search deals…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setActiveIdx(0) }}
          onKeyDown={(e) => {
            // The "Clear assignment" affordance (only present when onClear is
            // wired — i.e. the call already has an owner) lives at index -1,
            // BEFORE the first row, so arrows reach it and Enter triggers it.
            // When it's absent the model never reaches -1 (no dead aria id).
            const hasClear = !!onClear
            if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => nextAssignActiveIndex(i, 'down', rows.length, hasClear)) }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => nextAssignActiveIndex(i, 'up', rows.length, hasClear)) }
            else if (e.key === 'Enter') {
              e.preventDefault()
              if (activeIdx === -1) { if (onClear) onClear() }
              else { const row = rows[activeIdx]; if (row) onSelect(row) }
            }
            else if (e.key === 'Escape') { e.preventDefault(); onClose() }
          }}
        />
      </div>
      <div id={listboxId} role="listbox" aria-label="Deals and projects" className="phone-assign-body">
        {onClear && (
          <div
            id={clearDomId}
            className={`phone-assign-clear${activeIdx === -1 ? ' active' : ''}`}
            role="option"
            aria-selected={activeIdx === -1}
            onMouseEnter={() => setActiveIdx(-1)}
            onClick={onClear}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            <span>Clear assignment</span>
          </div>
        )}
        {loading ? (
          <>
            {[0, 1, 2].map((k) => (
              <div key={k} className="phone-assign-skel"><span className="phone-assign-skel-ic" /><span className="phone-assign-skel-line" /></div>
            ))}
          </>
        ) : query.trim().length >= 2 ? (
          rows.length > 0 ? renderRows() : (
            <div className="phone-assign-empty">No deals match &ldquo;{query.trim()}&rdquo; — try the customer name or address</div>
          )
        ) : suggestedDeals.length > 0 ? (
          <>
            <div className="phone-assign-section">Suggested — {suggestName || 'this contact'}</div>
            {renderRows()}
          </>
        ) : suggestName ? (
          <div className="phone-assign-empty">No deals for {suggestName} — type to search all deals</div>
        ) : (
          <div className="phone-assign-empty">Type to search deals…</div>
        )}
      </div>
    </>
  )

  if (variant === 'anchored') {
    const rect = liveRect || anchorRect
    if (!rect) return null
    // Wider than the phone wrap-up card so rows can breathe (deal title + client
    // + stage badge). Clamp to the viewport on narrow screens so it never
    // overflows the right edge.
    const WIDTH = Math.min(460, window.innerWidth - 16)
    const EST_H = 340
    let left = rect.left
    if (left + WIDTH > window.innerWidth - 8) left = Math.max(8, window.innerWidth - WIDTH - 8)
    let top = rect.bottom + 4
    if (top + EST_H > window.innerHeight - 8) top = Math.max(8, rect.top - EST_H - 4)
    return createPortal(
      <div
        ref={rootRef}
        className="phone-assign-overlay calls-assign-pop"
        data-marker={CALLS_LIST_ASSIGN_MARKER}
        style={{ position: 'fixed', left, top, width: WIDTH, zIndex: 10001 }}
      >
        {inner}
      </div>,
      document.body,
    )
  }

  // variant="phone" — frozen contract: the .phone-assign-overlay CSS floats it at
  // left:336 / bottom:24 / z 9999 next to the docked phone.
  return createPortal(
    <div ref={rootRef} className="phone-assign-overlay" data-testid="call-assign-link-v1">
      {inner}
    </div>,
    document.body,
  )
}
